import { useCallback, useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import fr from 'date-fns/locale/fr';
import 'react-datepicker/dist/react-datepicker.css';
import { LuTrash2, LuCheckCircle2, LuAlertCircle } from "react-icons/lu";
import "../../styles/backOffice/createFlashNews.css";
import { authFetch } from "../../utils/api";
import { toStoredDate, getStoredNow, getFullWrittenDateWithShortMonth, getHoursAndMinutes } from "../../utils/date";

registerLocale('fr', fr);

const EMPTY_FORM = { startDate: null, endDate: null, title: "", bodyText: "" };

//ÉTAT D'UNE FLASH NEWS PAR RAPPORT À MAINTENANT (heure locale, voir utils/date.js)
const getStatus = (news, now) => {
  if (now < new Date(news.startDate)) return { id: "upcoming", label: "À venir", className: "badge--dark" };
  if (now < new Date(news.endDate)) return { id: "current", label: "En cours", className: "badge--success" };
  return { id: "past", label: "Terminée", className: "" };
};

const formatDateTime = (date) => `${getFullWrittenDateWithShortMonth(date)} à ${getHoursAndMinutes(date)}`;


//CRÉATION ET GESTION DES FLASH NEWS (bandeau affiché en haut de l'accueil)
function CreateFlashNews() {

  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState(null); // { type, texts }

  const [allFlashNews, setAllFlashNews] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState("");
  const [listMessage, setListMessage] = useState(null);

  const updateField = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));

  //Liste à jour des flash news (rechargée après chaque création ou suppression)
  const loadAllFlashNews = useCallback(async () => {
    try {
      const response = await authFetch(`/flashNewsRoute/loadAllFlashNews`);
      setAllFlashNews(await response.json());
    } catch (error) {
      setListMessage({ type: "error", text: "Impossible de charger les flash news." });
    }
  }, []);

  useEffect(() => {
    loadAllFlashNews();
  }, [loadAllFlashNews]);

  // ******************* CRÉATION *******************
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.startDate || !form.endDate || !form.title.trim() || !form.bodyText.trim()) {
      setFormMessage({ type: "error", texts: ["Les dates de début et de fin d’affichage, le titre et le texte sont obligatoires."] });
      return;
    }
    if (form.endDate <= form.startDate) {
      setFormMessage({ type: "error", texts: ["La fin d’affichage doit être postérieure au début."] });
      return;
    }

    setIsSubmitting(true);
    setFormMessage(null);
    try {
      const response = await authFetch(`/flashNewsRoute/createFlashNews`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          //Heure saisie enregistrée telle quelle (voir la convention dans utils/date.js)
          startDateFromFront: toStoredDate(form.startDate),
          endDateFromFront: toStoredDate(form.endDate),
          titleFromFront: form.title.trim(),
          bodyTextFromFront: form.bodyText.trim(),
        }),
      });
      const body = await response.json();
      if (body.result === true) {
        setForm(EMPTY_FORM);
        setFormMessage({ type: "success", texts: body.success });
        loadAllFlashNews();
      } else {
        setFormMessage({ type: "error", texts: body.error });
      }
    } catch (error) {
      setFormMessage({ type: "error", texts: ["La flash news n’a pas pu être enregistrée. Merci de réessayer."] });
    }
    setIsSubmitting(false);
  };

  // ******************* SUPPRESSION (avec confirmation) *******************
  const deleteFlashNews = async (news) => {
    try {
      const response = await authFetch(`/flashNewsRoute/deleteFlashNews?_id=${news._id}`, { method: "DELETE" });
      const body = await response.json();
      setListMessage(body.result === true
        ? { type: "success", text: `« ${news.title} » a bien été supprimée.` }
        : { type: "error", text: body.error.join(" ") });
      loadAllFlashNews();
    } catch (error) {
      setListMessage({ type: "error", text: "La flash news n’a pas pu être supprimée. Merci de réessayer." });
    }
    setConfirmDeleteId("");
  };

  //Tri : en cours, puis à venir, puis terminées
  const now = getStoredNow();
  const statusOrder = { current: 0, upcoming: 1, past: 2 };
  const sortedFlashNews = allFlashNews
    .map((news) => ({ news, status: getStatus(news, now) }))
    .sort((a, b) => statusOrder[a.status.id] - statusOrder[b.status.id]);


  return (
    <div className="bo-page surface">
      <h2 className="title-xl bo-page_title">Flash News</h2>
      <p className="bo-page_intro">Une flash news s’affiche dans un bandeau en haut de la page d’accueil, pendant la période choisie.</p>

      {/* ******************* FORMULAIRE ******************* */}
      <form onSubmit={handleSubmit} noValidate>
        <fieldset className="form-section">
          <legend className="form-section_title">Nouvelle flash news</legend>

          <div className="field_row">
            <div className="field">
              <label className="field_label" htmlFor="flashStart">Début d’affichage <span className="field_required">*</span></label>
              <DatePicker
                id="flashStart"
                className="field_control"
                wrapperClassName="field_datepicker"
                selected={form.startDate}
                onChange={(date) => updateField("startDate", date)}
                selectsStart
                startDate={form.startDate}
                endDate={form.endDate}
                locale="fr"
                showTimeSelect
                timeIntervals={30}
                timeCaption="Heure"
                dateFormat="dd/MM/yyyy HH:mm"
                placeholderText="jj/mm/aaaa hh:mm"
              />
            </div>
            <div className="field">
              <label className="field_label" htmlFor="flashEnd">Fin d’affichage <span className="field_required">*</span></label>
              <DatePicker
                id="flashEnd"
                className="field_control"
                wrapperClassName="field_datepicker"
                selected={form.endDate}
                onChange={(date) => updateField("endDate", date)}
                selectsEnd
                startDate={form.startDate}
                endDate={form.endDate}
                minDate={form.startDate}
                locale="fr"
                showTimeSelect
                timeIntervals={30}
                timeCaption="Heure"
                dateFormat="dd/MM/yyyy HH:mm"
                placeholderText="jj/mm/aaaa hh:mm"
              />
            </div>
          </div>

          <div className="field">
            <label className="field_label" htmlFor="flashTitle">Titre <span className="field_required">*</span></label>
            <input className="field_control" id="flashTitle" type="text" maxLength={80} value={form.title} placeholder="Ex. : Gymnase Chantaco fermé mercredi" onChange={(e) => updateField("title", e.target.value)} />
          </div>

          <div className="field">
            <label className="field_label" htmlFor="flashText">Texte <span className="field_required">*</span></label>
            <textarea className="field_control" id="flashText" rows={3} maxLength={300} value={form.bodyText} placeholder="Ex. : Pour cause de vacances, le gymnase Chantaco sera fermé le mercredi 13 mars. Il n’y aura pas d’entraînement." onChange={(e) => updateField("bodyText", e.target.value)} />
            <p className="field_hint">Un texte court se lit mieux dans le bandeau ({form.bodyText.length}/300 caractères).</p>
          </div>

          {/* APERÇU DU BANDEAU TEL QU'IL APPARAÎTRA SUR L'ACCUEIL */}
          <div className="field">
            <span className="field_label">Aperçu</span>
            <div className="flash_bar flash_preview" aria-hidden="true">
              <div className="flash_bar_inner">
                <span className="flash_bar_tag">Flash</span>
                <div className="flash_bar_list">
                  <p className="flash_bar_text">
                    <strong>{form.title || "Titre de la flash news"}</strong>{` — ${form.bodyText || "Texte de la flash news"}`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </fieldset>

        {formMessage && (
          <div className={`msg ${formMessage.type === "success" ? "msg--success" : "msg--error"} flash_message`} role={formMessage.type === "success" ? "status" : "alert"}>
            {formMessage.type === "success" ? <LuCheckCircle2 aria-hidden="true" /> : <LuAlertCircle aria-hidden="true" />}
            <div>{formMessage.texts.map((text) => <p key={text}>{text}</p>)}</div>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn btn--primary btn--lg" disabled={isSubmitting}>
            {isSubmitting ? "Enregistrement…" : "Publier la flash news"}
          </button>
        </div>
      </form>

      {/* ******************* LISTE ******************* */}
      <section className="flash_list_section" aria-labelledby="flash-list-title">
        <h2 id="flash-list-title" className="form-section_title">Flash news enregistrées</h2>

        {listMessage && (
          <div className={`msg ${listMessage.type === "success" ? "msg--success" : "msg--error"} flash_message`} role="status">
            {listMessage.type === "success" ? <LuCheckCircle2 aria-hidden="true" /> : <LuAlertCircle aria-hidden="true" />}
            <p>{listMessage.text}</p>
          </div>
        )}

        {sortedFlashNews.length === 0
          ? <p className="flash_list_empty">Aucune flash news pour le moment.</p>
          : <ul className="flash_list">
            {sortedFlashNews.map(({ news, status }) => (
              <li key={news._id} className={`flash_item ${status.id === "past" ? "flash_item--past" : ""}`}>
                <div className="flash_item_body">
                  <div className="flash_item_heading">
                    <span className={`badge ${status.className}`}>{status.label}</span>
                    <p className="flash_item_title">{news.title}</p>
                  </div>
                  <p className="flash_item_text">{news.bodyText}</p>
                  <p className="flash_item_dates">Du {formatDateTime(news.startDate)} au {formatDateTime(news.endDate)}</p>
                </div>
                <div className="flash_item_actions">
                  {confirmDeleteId === news._id
                    ? <div className="delete_confirm" role="alert">
                      <p>Supprimer ?</p>
                      <button type="button" className="btn btn--danger" onClick={() => deleteFlashNews(news)}>Oui</button>
                      <button type="button" className="btn btn--secondary" onClick={() => setConfirmDeleteId("")}>Non</button>
                    </div>
                    : <button type="button" className="btn btn--secondary btn--icon btn--danger-outline" onClick={() => setConfirmDeleteId(news._id)} aria-label={`Supprimer la flash news « ${news.title} »`}>
                      <LuTrash2 aria-hidden="true" />
                    </button>}
                </div>
              </li>
            ))}
          </ul>}
      </section>
    </div>
  )
};

export default CreateFlashNews;
